import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * What the exhibitor page shows beside the booking: the passes of the stand, and its fit-out. The
 * registration page asks here for the stand that its badges were issued for.
 */

const base = '/v1/exhibitor/admin'
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

// --- passes -------------------------------------------------------------------------------------

export const passSchema = z.object({
  id: z.string(),
  holderName: z.string(),
  email: z.string(),
  jobTitle: z.string().nullable(),
  ticketTypeId: z.string().nullable().default(null),
  issuedTicketId: z.string().nullable(),
  registrationId: z.string().nullable().default(null),
})

export type Pass = z.infer<typeof passSchema>

export const passesSchema = z.object({
  entitled: z.number(),
  claimed: z.number(),
  items: z.array(passSchema),
})

export type Passes = z.infer<typeof passesSchema>

export function fetchPasses(applicationId: string, signal?: AbortSignal): Promise<Passes> {
  return api.get(`${base}/applications/${applicationId}/convention-passes`, {
    schema: passesSchema,
    ...withSignal(signal),
  })
}

/** The registrations that hold the stand's badges, each once, in the order of the passes. */
export function passRegistrations(passes: readonly Pass[]): string[] {
  return [
    ...new Set(
      passes.flatMap((pass) => (pass.registrationId === null ? [] : [pass.registrationId])),
    ),
  ]
}

export const standSchema = z.object({
  applicationId: z.string(),
  code: z.string().nullable(),
  companyName: z.string(),
})

export type Stand = z.infer<typeof standSchema>

/** The stand a registration of passes was issued for. The API answers 404 for any other. */
export function fetchStandOfRegistration(registrationId: string, signal?: AbortSignal) {
  return api.get(`${base}/convention-passes/registrations/${registrationId}`, {
    schema: standSchema,
    ...withSignal(signal),
  })
}

// --- fit-out ------------------------------------------------------------------------------------

export const FIT_OUT_STATUSES = ['draft', 'submitted', 'approved', 'rejected'] as const

const itemSchema = z.object({
  id: z.string(),
  status: z.enum(FIT_OUT_STATUSES),
  submittedAt: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewNote: z.string().nullable(),
})

export const fitOutSchema = z.object({
  contractor: itemSchema
    .extend({
      contractorName: z.string(),
      contactName: z.string().nullable(),
      contactEmail: z.string().nullable(),
      contactPhone: z.string().nullable(),
      attachments: z.number(),
    })
    .nullable(),
  fascia: itemSchema
    .extend({ fasciaName: z.string(), notes: z.string().nullable(), hasLogo: z.boolean() })
    .nullable(),
  designPlans: z.array(
    itemSchema.extend({
      title: z.string(),
      notes: z.string().nullable(),
      revision: z.number(),
      hasDrawing: z.boolean(),
    }),
  ),
})

export type FitOut = z.infer<typeof fitOutSchema>
export type FitOutKind = 'contractor' | 'design_plan' | 'fascia'

export function fetchFitOut(applicationId: string, signal?: AbortSignal): Promise<FitOut> {
  return api.get(`${base}/applications/${applicationId}/fit-out`, {
    schema: fitOutSchema,
    ...withSignal(signal),
  })
}

/** Approve, or reject with a note. The API refuses a rejection with no note. */
export function reviewFitOut(kind: FitOutKind, id: string, approved: boolean, note: string) {
  return api.post(`${base}/fit-out/${kind}/${id}/review`, {
    schema: z.object({ id: z.string() }),
    body: note.trim() === '' ? { approved } : { approved, note: note.trim() },
  })
}
